import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'

export const MAX_ATTACHMENTS = 4

// Web-safe formats only. There is no transcoding step, so anything a browser
// can't play natively would upload happily and then refuse to play. Kept in
// step with Closingcredits::ChatUpload::CONTENT_TYPES, which is what actually
// decides; this copy exists so the file picker can filter and so a bad pick is
// refused before it costs an upload.
export const ACCEPTED_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  audio: ['audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav', 'audio/x-wav', 'audio/webm', 'audio/ogg'],
  video: ['video/mp4', 'video/webm']
}

export const ALL_ACCEPTED = [...ACCEPTED_TYPES.image, ...ACCEPTED_TYPES.audio, ...ACCEPTED_TYPES.video]

const MAX_BYTES = {
  image: 8 * 1024 * 1024,
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024
}

const kindFor = (type) => {
  if (ACCEPTED_TYPES.image.includes(type)) return 'image'
  if (ACCEPTED_TYPES.audio.includes(type)) return 'audio'
  if (ACCEPTED_TYPES.video.includes(type)) return 'video'
  return null
}

const mb = (bytes) => Math.round(bytes / 1024 / 1024)

// A video row can hold two blob URLs, the file itself and the frame grabbed
// off it. Both leak for the life of the tab if only one is released.
const revokePreviews = (attachment) => {
  if (attachment.preview) URL.revokeObjectURL(attachment.preview)
  if (attachment.posterPreview) URL.revokeObjectURL(attachment.posterPreview)
}

const POSTER_MAX_EDGE = 640

// Reads how long an audio or video file runs, so the message can say "2:14"
// before anyone presses play, and for video grabs a frame to use as the
// poster.
//
// Grabbing the frame here rather than with ffmpeg on a worker is only possible
// because we accept web-safe formats only: the browser can decode everything
// we allow, by definition. It costs no job, no download back out of R2, and
// there is no window where the video sits in the room as a black rectangle.
//
// Best effort throughout. A file whose metadata the browser can't parse, or a
// frame it refuses to paint, resolves to nulls rather than blocking the upload.
const readMediaMeta = (file, objectUrl) => new Promise((resolve) => {
  const kind = kindFor(file.type)
  if (kind === 'image') { resolve({ duration: null, poster: null }); return }

  const el = document.createElement(kind === 'video' ? 'video' : 'audio')
  let settled = false
  const done = (value) => {
    if (settled) return
    settled = true
    el.removeAttribute('src')
    el.load?.()
    resolve(value)
  }

  const duration = () => (Number.isFinite(el.duration) ? Math.round(el.duration) : null)

  el.preload = 'metadata'
  el.muted = true
  el.playsInline = true
  el.onerror = () => done({ duration: null, poster: null })

  if (kind === 'audio') {
    el.onloadedmetadata = () => done({ duration: duration(), poster: null })
  } else {
    el.onloadedmetadata = () => {
      // One second in, or the midpoint of anything shorter. Frame zero of a
      // video is very often black or a fade-in, which makes for a poster that
      // says nothing about the clip.
      const target = Math.min(1, (el.duration || 2) / 2)
      el.onseeked = () => done({ duration: duration(), poster: grabFrame(el) })
      try { el.currentTime = target } catch { done({ duration: duration(), poster: null }) }
    }
  }

  el.src = objectUrl
  // A file the browser starts decoding and then stalls on would otherwise hold
  // the upload forever.
  setTimeout(() => done({ duration: duration(), poster: null }), 10000)
})

// Paints the current video frame to a canvas and returns it as a JPEG blob.
// Scaled down: a poster is a thumbnail behind a play button, and a full-size
// frame off a 4K video is a bigger upload than some of the videos.
const grabFrame = (el) => {
  try {
    const w = el.videoWidth
    const h = el.videoHeight
    if (!w || !h) return null

    const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(w, h))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(w * scale)
    canvas.height = Math.round(h * scale)
    canvas.getContext('2d').drawImage(el, 0, 0, canvas.width, canvas.height)

    return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.7))
  } catch {
    // A cross-origin frame taints the canvas and toBlob throws. Ours never is,
    // but a poster is not worth failing an upload over.
    return null
  }
}

// Holds the composer's pending attachments: a local preview the moment a file
// is chosen, swapped for the R2 key once the PUT lands.
//
// Each one is { id, kind, preview, key, progress, name, contentType, sizeBytes,
// durationSeconds }. `key` is null while the upload is in flight, which is what
// the composer watches to keep Send disabled: posting a message whose file is
// still uploading would store a key that 404s for everyone including the poster.
const useChatAttachments = () => {
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const [attachments, setAttachments] = useState([])

  const isMounted = useRef(false)
  const seq = useRef(0)
  // A synchronous mirror of `attachments`. State is what renders, but the count
  // has to be readable and writable in one go inside add(), which React state
  // cannot do: a setState updater can run twice under StrictMode, and starting
  // the uploads from in there uploaded every file twice.
  const listRef = useRef([])
  // In-flight XHRs, so navigating away cancels them instead of leaving a 500MB
  // upload running against a room nobody is looking at.
  const requests = useRef(new Map())

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      requests.current.forEach(xhr => xhr.abort())
      requests.current.clear()
      listRef.current.forEach(revokePreviews)
      listRef.current = []
    }
  }, [])

  // The single write path. Runs once per call rather than once per render, so
  // side effects can safely sit next to it.
  const mutate = useCallback((fn) => {
    listRef.current = fn(listRef.current)
    setAttachments(listRef.current)
  }, [])

  const warn = useCallback((title, detail) => {
    setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
      <p className='text-sm font-medium text-red-600'>{title}</p>
      <p className='mt-1 text-sm text-red-400'>{detail}</p>
    </div>)
  }, [setToast])

  // Releases the blob and cancels the upload as well as forgetting the row.
  const forget = useCallback((id) => {
    const xhr = requests.current.get(id)
    if (xhr) { xhr.abort(); requests.current.delete(id) }

    mutate(prev => {
      const going = prev.find(a => a.id === id)
      if (going) revokePreviews(going)
      return prev.filter(a => a.id !== id)
    })
  }, [mutate])

  const drop = forget

  // XHR rather than fetch: a 500MB video wants a progress bar, and fetch still
  // can't report upload progress.
  const putToR2 = (uploadUrl, file, id, onDone, onFail) => {
    const xhr = new window.XMLHttpRequest()
    requests.current.set(id, xhr)

    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', file.type)

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !isMounted.current) return
      const pct = Math.round((e.loaded / e.total) * 100)
      mutate(prev => prev.map(a => (a.id === id ? { ...a, progress: pct } : a)))
    }
    xhr.onload = () => {
      requests.current.delete(id)
      if (xhr.status >= 200 && xhr.status < 300) onDone()
      else onFail(`Storage returned ${xhr.status}.`)
    }
    xhr.onerror = () => { requests.current.delete(id); onFail('Network error while uploading.') }
    xhr.onabort = () => { requests.current.delete(id) }
    xhr.send(file)
  }

  // Sends the grabbed frame up alongside its video and resolves to the key,
  // or to null. A poster is a nicety: if any part of this fails the video is
  // still perfectly postable, so nothing here rejects.
  const uploadPoster = useCallback((blob, videoName) => new Promise((resolve) => {
    if (!blob) { resolve(null); return }

    putpostRequest('/api/v1/chat_uploads', 'POST', {
      filename: `${videoName || 'video'}-poster.jpg`,
      content_type: 'image/jpeg',
      size_bytes: blob.size,
      poster: true
    }, (err, data) => {
      if (err || !data?.uploadUrl) { resolve(null); return }

      window.fetch(data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob
      })
        .then(response => resolve(response.ok ? data.key : null))
        .catch(() => resolve(null))
    })
  }), [putpostRequest])

  const uploadOne = useCallback((file, id, meta) => {
    const fail = (detail) => {
      if (isMounted.current) forget(id)
      warn('Upload error', detail)
    }

    putpostRequest('/api/v1/chat_uploads', 'POST', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size
    }, (err, data) => {
      if (err || !data?.uploadUrl) {
        fail(err?.errors || err?.error || 'Could not get an upload URL.')
        return
      }

      putToR2(data.uploadUrl, file, id, () => {
        if (!isMounted.current) return
        // Not re-adding the row: it may already be gone if the poster hit the
        // trash can while this was in flight, and it should stay gone.
        mutate(prev => prev.map(a => (
          a.id === id
            ? { ...a, key: data.key, kind: data.kind, progress: 100, durationSeconds: meta.duration }
            : a
        )))

        // After the video, not before: the poster is small and the video is
        // not, and racing them would have the thumbnail competing with the
        // thing it is a thumbnail of for the same uplink.
        uploadPoster(meta.poster, file.name).then((posterKey) => {
          if (!posterKey || !isMounted.current) return
          mutate(prev => prev.map(a => (a.id === id ? { ...a, posterKey } : a)))
        })
      }, fail)
    })
  }, [putpostRequest, warn, forget, mutate, uploadPoster])

  // The one entry point. Called by the file picker, the paste handler and the
  // drop handler alike, so the rules about type, size and count live in one
  // place instead of three.
  const add = useCallback((files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return

    const wrongType = incoming.filter(f => kindFor(f.type) === null)
    if (wrongType.length > 0) {
      warn('Some files were skipped', 'Images, mp3, m4a, wav, ogg, mp4 and webm work here.')
    }

    const rightType = incoming.filter(f => kindFor(f.type) !== null)
    const tooBig = rightType.filter(f => f.size > MAX_BYTES[kindFor(f.type)])
    tooBig.forEach(f => warn(
      `${f.name} is too big`,
      `The limit for ${kindFor(f.type)} is ${mb(MAX_BYTES[kindFor(f.type)])}MB.`
    ))

    const usable = rightType.filter(f => f.size <= MAX_BYTES[kindFor(f.type)])
    if (usable.length === 0) return

    const slots = Math.max(0, MAX_ATTACHMENTS - listRef.current.length)
    if (slots === 0) {
      warn('Too many files', `You can attach up to ${MAX_ATTACHMENTS} to one message.`)
      return
    }
    if (usable.length > slots) {
      warn('Too many files', `Up to ${MAX_ATTACHMENTS} per message, kept the first ${slots}.`)
    }

    const taken = usable.slice(0, slots)
    const added = taken.map((file) => {
      seq.current += 1
      return {
        id: `att-${seq.current}`,
        kind: kindFor(file.type),
        preview: URL.createObjectURL(file),
        key: null,
        progress: 0,
        name: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        durationSeconds: null,
        posterKey: null,
        posterPreview: null
      }
    })

    mutate(prev => [...prev, ...added])
    // Duration is read off the local blob before the bytes go anywhere, so it
    // costs nothing extra and is available the moment the upload lands.
    taken.forEach((file, idx) => {
      const row = added[idx]
      readMediaMeta(file, row.preview).then(async (meta) => {
        // grabFrame hands back a promise for the blob, so the poster needs one
        // more await before it is something uploadable.
        const poster = await meta.poster
        // The same frame the message will use, shown in the composer tile so a
        // video is recognisable before it is sent rather than being one of
        // several identical film icons.
        if (poster && isMounted.current) {
          const posterPreview = URL.createObjectURL(poster)
          mutate(prev => prev.map(a => (a.id === row.id ? { ...a, posterPreview } : a)))
        }
        uploadOne(file, row.id, { duration: meta.duration, poster })
      })
    })
  }, [uploadOne, warn, mutate])

  // Pulls files out of a paste or a drop. Pasting a screenshot gives a
  // clipboard item with kind 'file' and no useful name; copying from another
  // page can give both a file and a text/html item, and we want only the file.
  const addFromDataTransfer = useCallback((dataTransfer) => {
    if (!dataTransfer) return false

    const files = Array.from(dataTransfer.files || []).filter(f => kindFor(f.type))
    if (files.length > 0) { add(files); return true }

    const items = Array.from(dataTransfer.items || [])
      .filter(i => i.kind === 'file' && kindFor(i.type))
      .map(i => i.getAsFile())
      .filter(Boolean)
    if (items.length > 0) { add(items); return true }

    return false
  }, [add])

  const clear = useCallback(() => {
    requests.current.forEach(xhr => xhr.abort())
    requests.current.clear()
    mutate(prev => {
      prev.forEach(revokePreviews)
      return []
    })
  }, [mutate])

  const uploading = attachments.some(a => !a.key)
  // What the message endpoint wants: keys, never URLs.
  const payload = attachments
    .filter(a => a.key)
    .map(a => ({
      key: a.key,
      kind: a.kind,
      name: a.name,
      contentType: a.contentType,
      sizeBytes: a.sizeBytes,
      durationSeconds: a.durationSeconds,
      posterKey: a.posterKey || null
    }))

  return { attachments, add, addFromDataTransfer, drop, clear, uploading, payload }
}

export default useChatAttachments
